import { useMemo } from 'react';
import { useApiWithRetry } from '../hooks/useApiWithRetry';
import { ProcessInstance, ProcessInstanceApiResponse, ProcessDefinition, ProcessDefinitionApiResponse, ProcessDefinitionFilters, ProcessInstanceDetailResponse, ProcessXmlResponse, RecreateUserTaskRequest, RecreateUserTaskResponse } from '../types/process';

/**
 * Serviço para gerenciar processos
 */
export class ProcessesService {
  private api: ReturnType<typeof useApiWithRetry>;

  constructor(api: ReturnType<typeof useApiWithRetry>) {
    this.api = api;
  }

  /**
   * Lista instâncias de processo por process-key com filtros e paginação
   */
  async getProcessInstancesByKey(
    tenantId: string,
    processKey: string,
    pagination: { page: number; size: number } = { page: 0, size: 10 },
    filters?: {
      finished?: boolean;
      active?: boolean;
      suspended?: boolean;
      withIncidents?: boolean;
    },
    processInstanceId?: string
  ): Promise<ProcessInstanceApiResponse> {
    if (!tenantId) {
      console.error('❌ tenantId é obrigatório para buscar instâncias de processo');
      throw new Error('tenantId é obrigatório para buscar instâncias de processo');
    }

    if (!processKey) {
      console.error('❌ processKey é obrigatório para buscar instâncias de processo');
      throw new Error('processKey é obrigatório para buscar instâncias de processo');
    }

    const params = new URLSearchParams();
    
    // Adicionar parâmetros de paginação e ordenação
    params.append('max_results', pagination.size.toString());
    params.append('first_result', (pagination.page * pagination.size).toString());
    params.append('sort_by', 'startTime');
    params.append('sort_order', 'desc');
    
    // Adicionar filtros se fornecidos
    if (filters?.finished !== undefined) {
      params.append('finished', filters.finished.toString());
    }
    if (filters?.active !== undefined) {
      params.append('active', filters.active.toString());
    }
    if (filters?.suspended !== undefined) {
      params.append('suspended', filters.suspended.toString());
    }
    if (filters?.withIncidents !== undefined) {
      params.append('with_incidents', filters.withIncidents.toString());
    }
    
    // Adicionar filtro por ID da instância de processo
    if (processInstanceId) {
      params.append('process_instance_id', processInstanceId);
    }

    const queryString = params.toString();
    const endpoint = `tenants/${tenantId}/processes/${processKey}/instances${queryString ? `?${queryString}` : ''}`;
    

    try {
      const response = await this.api.get<{ instances: ProcessInstance[]; total: number }>(endpoint);      
      // Mapear os dados da API para o formato esperado pelo componente
      const mappedProcesses = response.instances.map(process => ({
        ...process,
        // Adicionar campos que podem não estar presentes na API
        start_time: process.start_time || new Date().toISOString(),
        end_time: process.end_time || new Date().toISOString(),
        tenantId: tenantId,
        processDefinitionId: processKey
      }));
      
      // Transformar a resposta para o formato esperado
      return {
        processInstances: mappedProcesses,
        pagination: {
          page: pagination.page,
          size: pagination.size,
          total: response.total, // Usar o campo total retornado pela API
          totalPages: Math.ceil(response.total / pagination.size)
        },
        counters: {
          active: mappedProcesses.filter(p => p.status === 'ACTIVE').length,
          suspended: mappedProcesses.filter(p => p.status === 'SUSPENDED').length,
          completed: mappedProcesses.filter(p => p.status === 'COMPLETED').length,
          externallyTerminated: mappedProcesses.filter(p => p.status === 'EXTERNALLY_TERMINATED').length,
          internallyTerminated: mappedProcesses.filter(p => p.status === 'INTERNALLY_TERMINATED').length,
          total: mappedProcesses.length
        }
      };
    } catch (error) {
      console.error('Erro ao buscar instâncias de processo:', error);
      throw error;
    }
  }

  /**
   * Lista instâncias de processo com suporte a múltiplos process keys
   */
  async getProcessInstances(
    tenantId: string,
    pagination: { page: number; size: number } = { page: 0, size: 10 },
    filters?: {
      finished?: boolean;
      active?: boolean;
      suspended?: boolean;
      withIncidents?: boolean;
      processKeys?: string[];
    },
    processInstanceId?: string
  ): Promise<ProcessInstanceApiResponse> {
    if (!tenantId) {
      console.error('❌ tenantId é obrigatório para buscar instâncias de processo');
      throw new Error('tenantId é obrigatório para buscar instâncias de processo');
    }

    const params = new URLSearchParams();
    
    // Adicionar parâmetros de paginação e ordenação
    params.append('max_results', pagination.size.toString());
    params.append('first_result', (pagination.page * pagination.size).toString());
    params.append('sort_by', 'startTime');
    params.append('sort_order', 'desc');
    
    // Adicionar filtros se fornecidos
    if (filters?.finished !== undefined) {
      params.append('finished', filters.finished.toString());
    }
    if (filters?.active !== undefined) {
      params.append('active', filters.active.toString());
    }
    if (filters?.suspended !== undefined) {
      params.append('suspended', filters.suspended.toString());
    }
    if (filters?.withIncidents !== undefined) {
      params.append('with_incidents', filters.withIncidents.toString());
    }
    
    // Adicionar filtro por múltiplos process keys (comma-separated)
    if (filters?.processKeys && filters.processKeys.length > 0) {
      params.append('process_definition_key_in', filters.processKeys.join(','));
    }
    
    // Adicionar filtro por ID da instância de processo
    if (processInstanceId) {
      params.append('process_instance_id', processInstanceId);
    }

        const queryString = params.toString();
        const endpoint = `tenants/${tenantId}/process-instances${queryString ? `?${queryString}` : ''}`;
        

    try {
      const response = await this.api.get<{ instances: ProcessInstance[]; total: number }>(endpoint);      
      
      // Verificar se a resposta tem a estrutura esperada
      const instances = response.instances || [];
      const total = response.total || 0;
      
      // Mapear os dados da API para o formato esperado pelo componente
      const mappedProcesses = instances.map(process => ({
        ...process,
        // Adicionar campos que podem não estar presentes na API
        start_time: process.start_time || new Date().toISOString(),
        end_time: process.end_time || new Date().toISOString(),
        tenantId: tenantId,
        processDefinitionId: process.process_key
      }));
      
      // Transformar a resposta para o formato esperado
      return {
        processInstances: mappedProcesses,
        pagination: {
          page: pagination.page,
          size: pagination.size,
          total: total,
          totalPages: Math.ceil(total / pagination.size)
        },
        counters: {
          active: mappedProcesses.filter(p => p.status === 'ACTIVE').length,
          suspended: mappedProcesses.filter(p => p.status === 'SUSPENDED').length,
          completed: mappedProcesses.filter(p => p.status === 'COMPLETED').length,
          externallyTerminated: mappedProcesses.filter(p => p.status === 'EXTERNALLY_TERMINATED').length,
          internallyTerminated: mappedProcesses.filter(p => p.status === 'INTERNALLY_TERMINATED').length,
          total: mappedProcesses.length
        }
      };
    } catch (error) {
      console.error('Erro ao buscar instâncias de processo:', error);
      // Retornar resposta vazia em caso de erro para não quebrar o layout
      return {
        processInstances: [],
        pagination: {
          page: pagination.page,
          size: pagination.size,
          total: 0,
          totalPages: 0
        },
        counters: {
          active: 0,
          suspended: 0,
          completed: 0,
          externallyTerminated: 0,
          internallyTerminated: 0,
          total: 0
        }
      };
    }
  }


  /**
   * Lista definições de processo com filtros e paginação
   */
  async getProcessDefinitions(
    filters: ProcessDefinitionFilters = {},
    pagination: { page: number; size: number } = { page: 0, size: 10 }
  ): Promise<ProcessDefinitionApiResponse> {
    if (!filters.tenantId) {
      console.error('❌ tenantId é obrigatório para buscar definições de processo');
      throw new Error('tenantId é obrigatório para buscar definições de processo');
    }

    const params = new URLSearchParams();
    
    // Adicionar parâmetros de paginação e ordenação
    params.append('max_results', pagination.size.toString());
    params.append('first_result', (pagination.page * pagination.size).toString());
    params.append('sort_order', 'desc');
    params.append('sort_by', 'key');
    
    // Adicionar filtros adicionais se necessário
    if (filters.name) params.append('name', filters.name);
    if (filters.key) params.append('key', filters.key);
    if (filters.suspended !== undefined) params.append('suspended', filters.suspended.toString());

    const queryString = params.toString();
    const endpoint = `tenants/${filters.tenantId}/processes${queryString ? `?${queryString}` : ''}`;

    try {
      const response = await this.api.get<{ processes: ProcessDefinition[]; total: number }>(endpoint);
      
      // Mapear os dados da API para o formato esperado pelo componente
      const mappedDefinitions = response.processes.map(process => ({
        ...process,
        // Adicionar campos que podem não estar presentes na API
        createdAt: new Date().toISOString(), // Placeholder - ajustar quando a API fornecer
        updatedAt: new Date().toISOString(), // Placeholder - ajustar quando a API fornecer
        tenantId: filters.tenantId || ''
      }));
      
      // Transformar a resposta para o formato esperado
      return {
        definitions: mappedDefinitions,
        pagination: {
          page: pagination.page,
          size: pagination.size,
          total: response.total, // Usar o campo total retornado pela API
          totalPages: Math.ceil(response.total / pagination.size)
        },
        total: response.total
      };
    } catch (error) {
      console.error('❌ Erro ao buscar definições de processo:', error);
      console.error('❌ Detalhes do erro:', {
        message: error instanceof Error ? error.message : 'Erro desconhecido',
        status: error instanceof Error && 'status' in error ? (error as Error & { status: unknown }).status : 'N/A',
        stack: error instanceof Error ? error.stack : 'N/A'
      });
      throw error;
    }
  }

  /**
   * Busca detalhes de uma instância específica de processo
   */
  async getProcessInstanceDetail(
    tenantId: string,
    instanceId: string
  ): Promise<ProcessInstanceDetailResponse> {
    if (!tenantId) {
      console.error('❌ tenantId é obrigatório para buscar detalhes da instância');
      throw new Error('tenantId é obrigatório para buscar detalhes da instância');
    }

    if (!instanceId) {
      console.error('❌ instanceId é obrigatório para buscar detalhes da instância');
      throw new Error('instanceId é obrigatório para buscar detalhes da instância');
    }

    const endpoint = `tenants/${tenantId}/process-instances/${instanceId}`;

    try {
      const response = await this.api.get<ProcessInstanceDetailResponse>(endpoint);
      
      return response;
    } catch (error) {
      console.error('❌ Erro ao buscar detalhes da instância:', error);
      throw error;
    }
  }

  /**
   * Busca o XML do processo pelo ID da definição
   */
  async getProcessXml(
    tenantId: string,
    processId: string
  ): Promise<ProcessXmlResponse> {
    if (!tenantId) {
      console.error('❌ tenantId é obrigatório para buscar XML do processo');
      throw new Error('tenantId é obrigatório para buscar XML do processo');
    }

    if (!processId) {
      console.error('❌ processId é obrigatório para buscar XML do processo');
      throw new Error('processId é obrigatório para buscar XML do processo');
    }

    const endpoint = `tenants/${tenantId}/processes/${processId}/xml`;

    try {
      const response = await this.api.get<ProcessXmlResponse>(endpoint);
      
      return response;
    } catch (error) {
      console.error('❌ Erro ao buscar XML do processo:', error);
      throw error;
    }
  }

  /**
   * Busca os detalhes do erro (stacktrace) de um incidente
   */
  async getIncidentErrorDetails(
    tenantId: string,
    externalTaskId: string
  ): Promise<{ error_details: string }> {
    if (!tenantId) {
      console.error('❌ tenantId é obrigatório para buscar detalhes do erro');
      throw new Error('tenantId é obrigatório para buscar detalhes do erro');
    }

    if (!externalTaskId) {
      console.error('❌ externalTaskId é obrigatório para buscar detalhes do erro');
      throw new Error('externalTaskId é obrigatório para buscar detalhes do erro');
    }

    const endpoint = `tenants/${tenantId}/incidents/${externalTaskId}/error-details`;

    try {
      const response = await this.api.get<{ error_details: string }>(endpoint);
      
      return response;
    } catch (error) {
      console.error('❌ Erro ao buscar detalhes do erro do incidente:', error);
      throw error;
    }
  }

  /**
   * Recria uma tarefa de usuário cancelada ou com erro
   */
  async recreateUserTask(
    tenantId: string,
    instanceId: string,
    request: RecreateUserTaskRequest
  ): Promise<RecreateUserTaskResponse> {
    if (!tenantId) {
      console.error('❌ tenantId é obrigatório para recriar tarefa');
      throw new Error('tenantId é obrigatório para recriar tarefa');
    }

    if (!instanceId) {
      console.error('❌ instanceId é obrigatório para recriar tarefa');
      throw new Error('instanceId é obrigatório para recriar tarefa');
    }

    const endpoint = `tenants/${tenantId}/process-instances/${instanceId}/recreate-user-task`;

    try {
      const response = await this.api.post<RecreateUserTaskResponse, RecreateUserTaskRequest>(endpoint, request);
      
      return response;
    } catch (error) {
      console.error('❌ Erro ao recriar tarefa de usuário:', error);
      throw error;
    }
  }

  /**
   * Resolve (deleta) um incidente
   */
  async resolveIncident(
    tenantId: string,
    incidentId: string
  ): Promise<{ success: boolean; message: string }> {
    if (!tenantId) {
      console.error('❌ tenantId é obrigatório para resolver incidente');
      throw new Error('tenantId é obrigatório para resolver incidente');
    }

    if (!incidentId) {
      console.error('❌ incidentId é obrigatório para resolver incidente');
      throw new Error('incidentId é obrigatório para resolver incidente');
    }

    const endpoint = `tenants/${tenantId}/incidents/${incidentId}`;

    try {
      const response = await this.api.delete<{ success: boolean; message: string }>(endpoint);
      
      return response;
    } catch (error) {
      console.error('❌ Erro ao resolver incidente:', error);
      throw error;
    }
  }
}

/**
 * Hook para usar o serviço de processos
 */
export const useProcessesService = () => {
  const api = useApiWithRetry();
  return useMemo(() => new ProcessesService(api), []); // Usar useMemo para evitar recriação
};
